import PropTypes from "prop-types";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import SharedIDModal from "pages/Clients/sections/ClientHeros/SharedIDModal";
import api from "services/api";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

import bgImage from "assets/images/bg-user-info.jpg";

function PlatformHero({ clientKey, buttons, softwarePlatform }) {
  const { t } = useTranslation();
  const [sharedOpen, setSharedOpen] = useState(false);
  const [software, setSoftware] = useState(null);
  const stepKeys = ["step1", "step2", "step3", "step4"];

  useEffect(() => {
    if (!softwarePlatform) {
      setSoftware(null);
      return;
    }

    api
      .get(`/software/${softwarePlatform}/`)
      .then((res) => {
        setSoftware(res?.data || null);
      })
      .catch(() => {
        setSoftware(null);
      });
  }, [softwarePlatform]);

  return (
    <MKBox
      minHeight="52vh"
      width="100%"
      sx={({ breakpoints }) => ({
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "grid",
        placeItems: "center",
        paddingTop: "5rem",
        paddingBottom: "5rem",
        [breakpoints.down("md")]: {
          paddingTop: "6rem",
          paddingBottom: "6rem",
        },
      })}
    >
      <Container>
        <Grid container spacing={3} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={6} sx={{ textAlign: "center", mt: { xs: 2, md: 0 } }}>
            <MKTypography variant="h4" color="white" fontWeight="bold">
              {t(`clients.${clientKey}.hero.title`)}
            </MKTypography>
            <MKTypography variant="body2" color="white" opacity={0.82} mt={1}>
              {t(`clients.${clientKey}.hero.subtitle`)}
            </MKTypography>
            {software?.version ? (
              <MKTypography variant="body2" color="white" opacity={0.82} mt={0.5}>
                {t("clients.common.currentVersion")}: {software.version}
              </MKTypography>
            ) : null}
            <MKBox mt={2} display="flex" flexWrap="wrap" justifyContent="center" gap={1.5}>
              {buttons.map((button) => {
                const isSharedIdAction = button.action === "sharedAppleId";
                return (
                  <MKButton
                    key={button.labelKey}
                    variant="outlined"
                    color="white"
                    size="small"
                    component={isSharedIdAction ? "button" : "a"}
                    href={isSharedIdAction ? undefined : button.href}
                    target={button.external ? "_blank" : undefined}
                    rel={button.external ? "noopener noreferrer" : undefined}
                    onClick={isSharedIdAction ? () => setSharedOpen(true) : undefined}
                    sx={{
                      fontSize: "0.8rem",
                      px: 1.5,
                      minHeight: "40px",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    {t(button.labelKey)}
                  </MKButton>
                );
              })}
            </MKBox>
            <SharedIDModal open={sharedOpen} onClose={() => setSharedOpen(false)} />
          </Grid>

          <Grid item xs={12} md={6} sx={{ mt: { xs: 4, md: 0 } }}>
            <MKTypography variant="h5" color="white" fontWeight="bold">
              {t(`clients.${clientKey}.usage.title`)}
            </MKTypography>
            {stepKeys.map((stepKey, index) => (
              <MKTypography key={stepKey} variant="body2" color="white" opacity={0.86} mt={1}>
                {index + 1}. {t(`clients.${clientKey}.usage.${stepKey}`)}
              </MKTypography>
            ))}
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

PlatformHero.defaultProps = {
  buttons: [],
  softwarePlatform: "",
};

PlatformHero.propTypes = {
  clientKey: PropTypes.string.isRequired,
  softwarePlatform: PropTypes.string,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      labelKey: PropTypes.string.isRequired,
      external: PropTypes.bool,
      action: PropTypes.oneOf(["sharedAppleId"]),
    })
  ),
};

export default PlatformHero;
